import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { useGetRuntimeConfiguration } from "../hooks/useGetRuntimeConfiguration";

const EMPTY = "—";

const orEmpty = (value: string | null | undefined) =>
  value === null || value === undefined || value.trim() === "" ? EMPTY : value;

function StatusBadge({ configured }: { configured: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={
        configured
          ? "rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"
          : "rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700"
      }
    >
      {configured
        ? t("runtime_configuration.configured")
        : t("runtime_configuration.not_configured")}
    </span>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
      <span className="break-all text-sm font-medium text-gray-900 dark:text-white">{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-[#DCDCDC] bg-white p-4 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold text-[#1D1D1F]">{title}</h2>
      {children}
    </section>
  );
}

export function RuntimeConfigurationPage() {
  const { t } = useTranslation();
  const { data, error, loading, refetch } = useGetRuntimeConfiguration();

  const integrations = data
    ? (
        [
          ["jwt", data.integrations.jwt_configured],
          ["firebase", data.integrations.firebase_configured],
          ["loops", data.integrations.loops_configured],
          ["open_ai", data.integrations.open_ai_configured],
          ["pims", data.integrations.pims_configured],
          ["redis", data.integrations.redis_configured],
          ["smtp", data.integrations.smtp_configured],
          ["uvdesk", data.integrations.uvdesk_configured],
          ["stripe", data.integrations.stripe_configured],
          ["tango", data.integrations.tango_configured],
          ["twilio", data.integrations.twilio_enabled],
        ] as const
      ).map(([key, configured]) => ({
        label: t(`runtime_configuration.integrations.${key}`),
        configured,
      }))
    : [];

  return (
    <main className="min-h-screen bg-background p-4 md:p-6">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#004232] dark:text-white">
            {t("runtime_configuration.title")}
          </h1>
          <p className="mt-1 font-semibold text-[#6E7670] text-muted-foreground">
            {t("runtime_configuration.description")}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void refetch()}
          disabled={loading}
        >
          <RefreshCw className={loading ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"} />
          {t("runtime_configuration.refresh")}
        </Button>
      </div>

      {loading && !data ? (
        <p className="rounded-xl border border-[#DCDCDC] bg-white p-4 text-sm text-gray-500 shadow-sm">
          {t("runtime_configuration.loading")}
        </p>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="text-sm font-medium">{error}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => void refetch()}
            disabled={loading}
            className="mt-3"
          >
            {t("runtime_configuration.retry")}
          </Button>
        </div>
      ) : null}

      {data && !error ? (
        <div className="grid gap-4">
          <Section title={t("runtime_configuration.environment_title")}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field
                label={t("runtime_configuration.aspnetcore_environment")}
                value={orEmpty(data.aspnetcore_environment)}
              />
              <Field
                label={t("runtime_configuration.civicpoints_environment")}
                value={orEmpty(data.civicpoints_environment)}
              />
              <Field
                label={t("runtime_configuration.dotenv_file")}
                value={orEmpty(data.dotenv_file)}
              />
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t("runtime_configuration.dotenv_file_loaded")}
                </span>
                <span
                  className={
                    data.dotenv_file_loaded
                      ? "text-sm font-medium text-emerald-700"
                      : "text-sm font-medium text-amber-700"
                  }
                >
                  {data.dotenv_file_loaded
                    ? t("runtime_configuration.loaded")
                    : t("runtime_configuration.not_loaded")}
                </span>
              </div>
            </div>
          </Section>

          <Section title={t("runtime_configuration.endpoints_title")}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field
                label={t("runtime_configuration.port")}
                value={String(data.port)}
              />
              <Field
                label={t("runtime_configuration.public_url")}
                value={orEmpty(data.public_url)}
              />
              <Field
                label={t("runtime_configuration.civic_web_domain")}
                value={orEmpty(data.civic_web_domain)}
              />
              <Field
                label={t("runtime_configuration.reset_password_url")}
                value={orEmpty(data.frontend_reset_password_url)}
              />
            </div>
          </Section>

          <Section title={t("runtime_configuration.infrastructure_title")}>
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t("runtime_configuration.postgres")}
                  </h3>
                  <StatusBadge configured={data.postgres.configured} />
                </div>
                <div className="grid gap-3">
                  <Field
                    label={t("runtime_configuration.host")}
                    value={orEmpty(data.postgres.host)}
                  />
                  <Field
                    label={t("runtime_configuration.port")}
                    value={orEmpty(data.postgres.port)}
                  />
                  <Field
                    label={t("runtime_configuration.database")}
                    value={orEmpty(data.postgres.database)}
                  />
                  <Field
                    label={t("runtime_configuration.ssl_mode")}
                    value={orEmpty(data.postgres.ssl_mode)}
                  />
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t("runtime_configuration.mongodb")}
                  </h3>
                  <StatusBadge configured={data.mongodb.configured} />
                </div>
                <div className="grid gap-3">
                  <Field
                    label={t("runtime_configuration.database")}
                    value={orEmpty(data.mongodb.database)}
                  />
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t("runtime_configuration.blob_storage")}
                  </h3>
                  <StatusBadge configured={data.blob_storage.configured} />
                </div>
                <div className="grid gap-3">
                  <Field
                    label={t("runtime_configuration.account_name")}
                    value={orEmpty(data.blob_storage.account_name)}
                  />
                  <Field
                    label={t("runtime_configuration.container_name")}
                    value={orEmpty(data.blob_storage.container_name)}
                  />
                  <Field
                    label={t("runtime_configuration.public_base_url")}
                    value={orEmpty(data.blob_storage.public_base_url)}
                  />
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      {t("runtime_configuration.public_access")}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {data.blob_storage.public_access_enabled
                        ? t("runtime_configuration.enabled")
                        : t("runtime_configuration.disabled")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          <Section title={t("runtime_configuration.integrations_title")}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {integrations.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2"
                >
                  <span className="text-sm font-medium text-gray-900">{item.label}</span>
                  <StatusBadge configured={item.configured} />
                </div>
              ))}
            </div>
          </Section>
        </div>
      ) : null}
    </main>
  );
}
